import { Outlet, useParams } from "@tanstack/react-router";

import { Breadcrumbs } from "@components/ui/breadcrumb";
import { Skeleton } from "@components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@components/ui/tabs";

import { useCapabilities, useMe } from "@hooks/queries/use-me";

import { CASE_TABS } from "./constants";
import { CaseDetailProvider, useCaseDetail } from "./context";
import { useCaseDetailTabs } from "./use-case-detail-tabs";

/**
 * Layout shell for a single case within Report Studio.
 *
 * Renders a two-card header unit — a breadcrumb card (breadcrumbs + a bulleted
 * case-details subtitle) stacked directly on top of a navigation card (pill tab
 * bar on the left, per-tab action buttons on the right). The child route
 * (matched by the active tab) supplies its `rightContent` (and `description`)
 * via the CaseDetail context.
 *
 * Child pages are rendered via `<Outlet />`, keyed by case number (MDRS-155)
 * so every tab fully remounts on a case switch instead of reusing the previous
 * case's instance. Switching cases is a route-param-only change on the same
 * path (e.g. `/report-studio/$id/assessment-prep`), so without the key
 * TanStack Router reuses each tab's component and any per-case local state
 * (selected file, search filters, modal state, etc.) — and the effects tied to
 * it — would leak across the switch, as happened with the Assessment Scribe
 * transcript (MDRS-155). This key makes that reset the default for every tab,
 * current and future, rather than something each page has to opt into.
 *
 * Must be wrapped in `CaseDetailProvider` so that child routes can push
 * dynamic `description` and `rightContent` into the header via context.
 */
const CaseDetailContent = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };

  // Content set by child routes to customise the header per-tab. `rightContent`
  // renders as the nav card's action buttons.
  const { rightContent } = useCaseDetail();

  // Access levels v1: only show tabs the user has the capability for (a
  // referrer sees just Assessment Prep).
  const { can } = useCapabilities();
  // Referrers get "Request documents" as an inline section on the case detail
  // (below Case Details), not a tab — so hide that tab for them.
  const { data: me } = useMe();
  const isReferrer = me?.user?.user_type === "referrer";
  const visibleTabs = CASE_TABS.filter(
    (tab) => can(tab.capability) && !(isReferrer && tab.id === "request-documents")
  );

  // Tab navigation state derived from the current URL
  const {
    activeTab,
    breadcrumbs,
    caseSubtitle,
    isCaseLoading,
    isOnOverview,
    handleTabChange,
  } = useCaseDetailTabs();

  // Left padding is intentionally omitted below: the SideNavigation's `m-2`
  // already supplies the 8px gutter between the nav and this content, so
  // adding `pl-2` here would double it to 16px (design gap is 8px).
  return (
    <div className="grid h-dvh grid-rows-[auto_1fr] gap-2 py-2 pr-2">
      <div>
        {/* Breadcrumb card — top of the joined header unit */}
        <div className="flex flex-col gap-2 rounded-t-xl border border-border bg-background-white p-4">
          {isCaseLoading ? (
            <>
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-2/3" />
            </>
          ) : (
            <>
              <Breadcrumbs items={breadcrumbs} />
              {caseSubtitle && (
                <p className="text-sm text-foreground-muted">{caseSubtitle}</p>
              )}
            </>
          )}
        </div>

        {/* Navigation card — pill tabs (left) + action buttons (right).
            Shares its top border with the card above to read as one unit.
            Access levels v1: render only the tabs the user can see. */}
        <div className="flex items-center justify-between gap-4 rounded-b-xl border border-t-0 border-border bg-background-white px-4 py-3">
          <div className="flex flex-col gap-2">
            {isOnOverview && (
              <span className="text-sm font-medium text-foreground">
                Select a step to begin
              </span>
            )}
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList variant="pill">
                {visibleTabs.map((tab) => (
                  <TabsTrigger key={tab.id} variant="pill" value={tab.id}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {rightContent && <div className="shrink-0">{rightContent}</div>}
        </div>
      </div>

      {/* Child route content fills remaining vertical space.
          min-h-0 is required here: this div is a CSS grid item in the
          `1fr` row of the parent grid, and grid items — like flex items —
          default to `min-height: auto`, meaning they'll grow to fit their
          content instead of respecting the track's allocated space. Without
          it, tall content (e.g. a long live transcript) grows this
          container past the viewport instead of being clipped/scrolled by
          the descendants that expect a real bounded height. */}
      <div className="h-full min-h-0 overflow-hidden">
        <Outlet key={caseNumber} />
      </div>
    </div>
  );
};

const ReportStudioCaseDetail = () => {
  return (
    <CaseDetailProvider>
      <CaseDetailContent />
    </CaseDetailProvider>
  );
};

export default ReportStudioCaseDetail;
